import type { KeyboardEvent, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import s from './DataTable.module.css';

export type Column<T> = {
  key: string;
  header: ReactNode;
  /** The grid track, e.g. 'minmax(0,2fr)' or '64px'. */
  width: string;
  render: (row: T) => ReactNode;
};

type Props<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Whole-row navigation; adds the hover fill, radius and a trailing chevron column. */
  onRowClick?: (row: T) => void;
  /** Inner min-width; the card scrolls horizontally below it. */
  minWidth?: number;
  /** Optional title row (15px/500) above the header, as in «Подписки». */
  title?: ReactNode;
  /** 13px rows and the compact header (8px 14px), as in the subscriptions table. */
  compact?: boolean;
  empty?: ReactNode;
  /** Accessible name of the table. */
  label: string;
  className?: string;
};

/** DataTable (DESIGN_SPEC §3): caps header, rows with .04 dividers, hover #1C1C1F, trailing chevron. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  minWidth = 900,
  title,
  compact,
  empty,
  label,
  className,
}: Props<T>) {
  const template = columns.map((c) => c.width).join(' ') + (onRowClick ? ' 24px' : '');
  const onKey = (row: T) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick?.(row);
    }
  };
  return (
    <div className={cx(s.wrap, className)}>
      <div style={{ minWidth }} role="table" aria-label={label}>
        {title && <div className={s.title}>{title}</div>}
        <div className={cx(s.head, compact && s.headCompact)} style={{ gridTemplateColumns: template }} role="row">
          {columns.map((c) => (
            <span key={c.key} role="columnheader">
              {c.header}
            </span>
          ))}
          {onRowClick && <span role="columnheader" />}
        </div>
        {rows.map((row) => (
          <div
            key={rowKey(row)}
            role="row"
            className={cx(s.row, compact && s.row13, onRowClick && s.clickable)}
            style={{ gridTemplateColumns: template }}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={onRowClick ? onKey(row) : undefined}
            tabIndex={onRowClick ? 0 : undefined}
          >
            {columns.map((c) => (
              <div key={c.key} role="cell" style={{ display: 'contents' }}>
                {c.render(row)}
              </div>
            ))}
            {onRowClick && <Icon name="chevron-right" size={16} className={s.chevron} />}
          </div>
        ))}
        {rows.length === 0 && empty && <div className={s.empty}>{empty}</div>}
      </div>
    </div>
  );
}
